import { Form, Input, InputNumber, Modal, notification } from 'antd';
import { useEffect, useState } from 'react';
import { formatDate, formatDateTime } from '../utils';
import APIClient from '../api/api';
import { ApiToken } from '../api/models';
import { Button, Column, DataTable, StatusTag } from '../components/ui';
import { PageHead, PageToolbar } from '../layout';

type TokenState = 'active' | 'expired' | 'revoked';

function tokenState(t: ApiToken): TokenState {
  if (t.revokedAt) return 'revoked';
  if (t.expiresAt && t.expiresAt.getTime() < Date.now()) return 'expired';
  return 'active';
}

const ApiTokens = () => {
  const [tokens, setTokens] = useState<ApiToken[]>();
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);
  const [createForm] = Form.useForm();

  useEffect(() => {
    loadTokens();
  }, []);

  async function loadTokens() {
    try {
      setLoading(true);
      const resp = await APIClient.listApiTokens();
      setTokens(resp.tokens);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load API tokens.' });
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(values: any) {
    try {
      setCreating(true);
      const resp = await APIClient.createApiToken({
        name: values.name.trim(),
        expiresInDays: values.expiresInDays ?? undefined,
      });
      setCreateOpen(false);
      createForm.resetFields();
      setCreatedSecret(resp.token);
      loadTokens();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to create API token.',
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    if (!revoking) return;
    try {
      await APIClient.revokeApiToken(revoking.id);
      notification.success({ message: 'API token revoked.' });
      setRevoking(null);
      loadTokens();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error || 'Failed to revoke API token.',
      });
    }
  }

  async function copySecret() {
    if (!createdSecret) return;
    try {
      await navigator.clipboard.writeText(createdSecret);
      notification.success({ message: 'Token copied to clipboard.' });
    } catch {
      notification.error({ message: 'Could not copy — select and copy manually.' });
    }
  }

  const columns: Column<ApiToken>[] = [
    {
      key: 'name',
      title: 'Name',
      render: (t) => <span className="text-[13px] text-text font-medium">{t.name}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      width: 110,
      render: (t) => {
        const s = tokenState(t);
        return (
          <StatusTag tone={s === 'active' ? 'success' : s === 'expired' ? 'neutral' : 'danger'}>
            {s}
          </StatusTag>
        );
      },
    },
    {
      key: 'lastUsedAt',
      title: 'Last used',
      width: 170,
      render: (t) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {t.lastUsedAt ? formatDateTime(t.lastUsedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'expiresAt',
      title: 'Expires',
      width: 140,
      render: (t) => (
        <span className="font-mono text-[12px] text-text-2 tabular-nums">
          {t.expiresAt ? formatDate(t.expiresAt) : 'never'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      width: 140,
      render: (t) => (
        <span className="font-mono text-[12px] text-text-3 tabular-nums">
          {formatDate(t.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      width: 120,
      render: (t) =>
        tokenState(t) === 'active' ? (
          <div className="flex items-center justify-end">
            <Button size="sm" variant="ghost" onClick={() => setRevoking(t)}>
              Revoke
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHead
        title="API Tokens"
        subtitle="Long-lived bearer tokens for programmatic access, in place of the login session. You can only see and manage your own."
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Create token
          </Button>
        }
      />
      <PageToolbar>
        <span className="ml-auto text-[11px] text-text-3 font-mono">
          {tokens ? (
            <>
              total <b className="text-text-2">{tokens.length}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={tokens ?? []}
        rowKey={(t) => String(t.id)}
        loading={loading}
        emptyMessage="No API tokens."
      />

      <Modal
        title="Create API token"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        okText="Create"
        confirmLoading={creating}
      >
        <Form form={createForm} onFinish={handleCreate} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please name this token' }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Expires in days (optional)"
            name="expiresInDays"
            rules={[{ type: 'number', min: 1, max: 3650, message: '1–3650 days' }]}
          >
            <InputNumber min={1} max={3650} style={{ width: '100%' }} placeholder="Leave empty for no expiry" />
          </Form.Item>
          <div className="text-[12px] text-text-3">
            The token is shown once, right after creation. It carries your account's
            access. Revoke it any time without changing your password.
          </div>
        </Form>
      </Modal>

      <Modal
        title="Copy your API token"
        open={!!createdSecret}
        onCancel={() => setCreatedSecret(null)}
        onOk={() => setCreatedSecret(null)}
        okText="Done"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <div className="text-[13px] text-text-2 mb-3">
          This is the only time the token is shown. Copy it now and store it
          somewhere safe. If you lose it, revoke it and create a new one.
        </div>
        {createdSecret && (
          <div className="flex items-center gap-2">
            <Input readOnly value={createdSecret} className="font-mono text-[12px]" />
            <Button size="sm" variant="subtle" onClick={copySecret}>
              Copy
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        title="Revoke API token"
        open={!!revoking}
        onCancel={() => setRevoking(null)}
        onOk={handleRevoke}
        okText="Revoke"
        okButtonProps={{ danger: true }}
      >
        {revoking && (
          <div className="text-[13px] text-text-2">
            Revoke <b className="text-text">{revoking.name}</b>? Any client using this
            token will stop working immediately. This cannot be undone.
          </div>
        )}
      </Modal>
    </>
  );
};

export default ApiTokens;
